/**
 * Normalizes the raw pool website payload into the typed `SdkConfig`.
 *
 * All loose coercion of pool JSON (string booleans, numeric strings, missing
 * fields) happens here and only here — downstream code reads typed fields.
 *
 * Pixels are not fully resolved here: `config.pixels` starts empty (or as
 * init-supplied pixels only). Init resolves the active ad account and merges
 * that account's pixel with `init({ pixels })`.
 */

import { warn } from '../../util/log';
import { tryParseSdkPixelForConfig } from './pixelParse';
import { TRACKER_BASE_URLS } from './constants';
import type {
  AdAccountConfig,
  CmpInfo,
  GeoInfo,
  Pixel,
  PoolAdAccount,
  PoolWebsitePayload,
  SdkConfig,
  WebsiteInfo,
} from './types';

export interface NormalizeConfigOptions {
  /** Host-supplied pixels (`init({ pixels })`) — always loaded. */
  pixels?: Pixel[];
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asSettingsMap(raw: unknown): Record<string, unknown> {
  if (!isObject(raw)) return {};
  return raw;
}

function stringSetting(settings: Record<string, unknown>, key: string): string | undefined {
  const value = settings[key];
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string' && value.trim() !== '') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

/** Accepts an object or a JSON-encoded string; keeps non-empty string values. */
function readStringMap(value: unknown): Record<string, string> | undefined {
  let raw = value;
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return undefined;
    }
  }
  if (!isObject(raw)) return undefined;
  const out: Record<string, string> = {};
  for (const [key, entry] of Object.entries(raw)) {
    if (typeof entry === 'string' && entry.trim() !== '') out[key] = entry.trim().toLowerCase();
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function readNumericLoose(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function readBooleanLoose(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    if (s === '1' || s === 'true' || s === 'yes') return true;
    if (s === '0' || s === 'false' || s === 'no') return false;
  }
  return undefined;
}

function scalarToString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === 'null') return undefined;
    return trimmed;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

/* -------------------------------------------------------------------------- */
/* Pixels                                                                      */
/* -------------------------------------------------------------------------- */

function normalizePlatformForPixel(platformRaw: unknown): string {
  if (typeof platformRaw !== 'string' || !platformRaw.trim()) return '';
  return platformRaw.trim().toLowerCase();
}

function mapPoolPlatformToSdk(platform: string): string | undefined {
  switch (platform) {
    case 'facebook':
    case 'fb':
    case 'meta':
      return 'facebook';
    case 'google':
    case 'google_ads':
      return 'google';
    case 'pinterest':
      return 'pinterest';
    case 'quora':
      return 'quora';
    case 'taboola':
      return 'taboola';
    case 'outbrain':
      return 'outbrain';
    case 'baidu':
      return 'baidu';
    default:
      return undefined;
  }
}

function pixelFromAdAccountEntry(entry: Record<string, unknown>): Pixel | null {
  if (entry.active === false) return null;
  const rawPlatform = normalizePlatformForPixel(entry.platform);
  const sdkPlatform = mapPoolPlatformToSdk(rawPlatform);
  if (!sdkPlatform) return null;
  const pixelId = scalarToString(entry.pixel_id);
  if (!pixelId) return null;

  const base: Record<string, unknown> = { platform: sdkPlatform, pixel_id: pixelId };

  // cent milestone → platform label; yoto reads it off the ad account for every platform
  const valuePixel = entry.value_pixel;
  if (isObject(valuePixel)) {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(valuePixel)) {
      const s = scalarToString(value);
      if (s) out[key] = s;
    }
    if (Object.keys(out).length) base.value_pixel = out;
  }

  if (sdkPlatform === 'google') {
    const tag = scalarToString(entry.pixel_tag_id);
    if (tag) base.pixel_tag_id = tag;
  }
  if (sdkPlatform === 'taboola') {
    const fromAccountType = scalarToString(entry.account_type)?.toLowerCase();
    const fromLayout =
      typeof entry.layout === 'string' ? entry.layout.trim().toLowerCase() : '';
    base.account_type =
      fromAccountType === 'yahoo' || fromLayout === 'yahoo' ? 'yahoo' : 'taboola';
    const tag = scalarToString(entry.pixel_tag_id);
    if (tag) base.pixel_tag_id = tag;
  }
  return tryParseSdkPixelForConfig(base);
}

/** Merge account pixel(s) with host `init({ pixels })`, deduped by platform:id. */
export function mergePixels(fromAccount: Pixel[], fromInit: Pixel[]): Pixel[] {
  const seen = new Set(fromAccount.map((p) => `${p.platform}:${p.pixel_id}`));
  const out = [...fromAccount];
  for (const pixel of fromInit) {
    const key = `${pixel.platform}:${pixel.pixel_id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(pixel);
  }
  return out;
}

export function pixelsFromActiveAccount(account: AdAccountConfig | null | undefined): Pixel[] {
  return account?.pixel ? [account.pixel] : [];
}

/* -------------------------------------------------------------------------- */
/* Ad accounts (attribution slice)                                             */
/* -------------------------------------------------------------------------- */

function adAccountFromEntry(entry: PoolAdAccount): AdAccountConfig | null {
  if (entry.active === false) return null;
  const platform = typeof entry.platform === 'string' ? entry.platform.trim().toLowerCase() : '';
  if (!platform) return null;
  const sourceMediumRaw = entry.source_medium;
  if (!Array.isArray(sourceMediumRaw) || sourceMediumRaw.length === 0) return null;

  const account: AdAccountConfig = {
    platform,
    sourceMedium: sourceMediumRaw.map((s) => String(s).toLowerCase()),
  };
  const pixel = pixelFromAdAccountEntry(entry as unknown as Record<string, unknown>);
  if (pixel) {
    account.pixel = pixel;
  } else if (scalarToString(entry.pixel_id)) {
    warn('skipping ad account pixel (unknown platform?)', entry);
  }
  const forceTag = scalarToString(entry.force_ecom_amazon_tag);
  if (forceTag) account.forceEcomAmazonTag = forceTag;
  const adTagChannelBucket = scalarToString(entry.eatcb ?? entry.traffic_channels_bucket);
  if (adTagChannelBucket) account.adTagChannelBucket = adTagChannelBucket;
  const sessionTagChannelBucket = scalarToString(entry.estcb ?? entry.session_traffic_channels_bucket);
  if (sessionTagChannelBucket) account.sessionTagChannelBucket = sessionTagChannelBucket;
  const adTagBucketMode = scalarToString(entry.eatcbm);
  if (adTagBucketMode) account.adTagBucketMode = adTagBucketMode;
  const sessionTagBucketMode = scalarToString(entry.estcbm);
  if (sessionTagBucketMode) account.sessionTagBucketMode = sessionTagBucketMode;
  const adTagParentTag = scalarToString(entry.eatcbpt);
  if (adTagParentTag) account.adTagParentTag = adTagParentTag;
  const sessionTagParentTag = scalarToString(entry.estcbpt);
  if (sessionTagParentTag) account.sessionTagParentTag = sessionTagParentTag;
  if (typeof entry.cpc_multiplier === 'number' && Number.isFinite(entry.cpc_multiplier)) {
    account.cpcMultiplier = entry.cpc_multiplier;
  }
  return account;
}

function adAccountsFromPayload(raw: unknown): AdAccountConfig[] {
  if (!Array.isArray(raw)) return [];
  const out: AdAccountConfig[] = [];
  for (const row of raw) {
    if (!isObject(row)) continue;
    const account = adAccountFromEntry(row as PoolAdAccount);
    if (account) out.push(account);
  }
  return out;
}

/* -------------------------------------------------------------------------- */
/* Sections                                                                    */
/* -------------------------------------------------------------------------- */

const PUBLIC_SAFE_SETTING_KEYS = [
  'ecom_active',
  'ecom_amazon_default_tag',
  'ecom_amazon_market',
  'ecom_tags_prefix',
  'ecom_account_id',
  'privacy_url',
  'about_url',
  'tos_url',
  'ccpa_url',
  'donotsell_url',
  'footer_copyrights',
  'support_email',
] as const;

function publicSettingsSubset(settings: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of PUBLIC_SAFE_SETTING_KEYS) {
    if (key in settings) out[key] = settings[key];
  }
  return out;
}

function readWebsiteInfo(website: Record<string, unknown>): WebsiteInfo {
  const id = typeof website.id === 'number' ? website.id : Number(website.id);
  const name = typeof website.name === 'string' ? website.name : '';
  const domain = typeof website.domain === 'string' ? website.domain : '';
  const root_domain = typeof website.root_domain === 'string' ? website.root_domain : '';
  const theme = typeof website.theme === 'string' ? website.theme : 'default';
  if (!Number.isFinite(id) || !domain || !root_domain) {
    throw new Error('serve_website: website id / domain / root_domain required');
  }
  const out: WebsiteInfo = { id, name, domain, root_domain, theme };
  const logo_url = typeof website.logo_url === 'string' ? website.logo_url.trim() : '';
  if (logo_url) out.logo_url = logo_url;
  const fonts_params = typeof website.fonts_params === 'string' ? website.fonts_params.trim() : '';
  if (fonts_params) out.fonts_params = fonts_params;
  return out;
}

function readGeoInfo(raw: unknown): GeoInfo {
  const geo = isObject(raw) ? raw : {};
  // Left empty when the payload carries no geo: `cc=` on the wire falls through
  // to the tracker's own CF-IPCountry lookup, which a placeholder would block.
  const country = typeof geo.country === 'string' ? geo.country.trim().toUpperCase() : '';
  const region = typeof geo.region === 'string' && geo.region.trim() ? geo.region.trim() : undefined;
  const cc_bmi = readNumericLoose(geo.cc_bmi ?? geo.ccBMI);

  const out: GeoInfo = { country };
  if (region) out.region = region;
  if (cc_bmi !== undefined) out.cc_bmi = cc_bmi;
  return out;
}

function readCmpInfo(raw: unknown): CmpInfo {
  const cmp = isObject(raw) ? raw : {};
  return {
    required: readBooleanLoose(cmp.required) ?? false,
    ccpa_applicable: readBooleanLoose(cmp.ccpa_applicable) ?? false,
  };
}

/* -------------------------------------------------------------------------- */
/* Entry point                                                                 */
/* -------------------------------------------------------------------------- */

/** `payload` is the inner `result` object from pool `serve_website` (not the envelope). */
export function normalizeSdkConfig(
  payload: PoolWebsitePayload,
  opts: NormalizeConfigOptions = {},
): SdkConfig {
  const rawWebsite = payload.website;
  if (!isObject(rawWebsite)) throw new Error('serve_website: missing website');

  const website = readWebsiteInfo(rawWebsite);
  const settings = asSettingsMap(payload.settings);
  const ecomActive = readBooleanLoose(settings.ecom_active) ?? false;

  // Activity resolution: website settings first, then the activity the config
  // itself enables (ecom → 5/e1). BI classifies every event by these — a blind
  // '1' default lands ecom traffic under Activity=MBP and breaks rollups
  // against media-buying rows.
  const analytics: SdkConfig['analytics'] = {
    trackers: [...TRACKER_BASE_URLS],
    activityId:
      stringSetting(settings, 'analytics_activity_id') ?? (ecomActive ? '5' : '1'),
    activityModel:
      stringSetting(settings, 'analytics_activity_model') ?? (ecomActive ? 'e1' : 'G1'),
  };
  const ociSampleRate = readNumericLoose(settings.oci_tracker_sample_rate);
  if (ociSampleRate !== undefined) {
    analytics.ociSampleRate = ociSampleRate;
  }
  const predictionModifier = readNumericLoose(settings.prediction_modifier);
  if (predictionModifier !== undefined) {
    analytics.predictionModifier = predictionModifier;
  }
  const dfpCampaignAdvertisers = readStringMap(settings.dfp_campaign_ids);
  if (dfpCampaignAdvertisers) {
    analytics.dfpCampaignAdvertisers = dfpCampaignAdvertisers;
  }
  // admin key is the inverted disable flag; absent means events are on
  analytics.fbReportEvents = !(readBooleanLoose(settings.facebook_pixel_disable_browser_events) ?? false);

  return {
    website,
    geo: readGeoInfo(payload.geo),
    cmp: readCmpInfo(payload.cmp),
    analytics,
    // Active-account pixel is merged in init after sourceMedium resolve.
    pixels: [...(opts.pixels ?? [])],
    adAccounts: adAccountsFromPayload(payload.ad_accounts),
    ecom: {
      active: ecomActive,
      amazonDefaultTag: stringSetting(settings, 'ecom_amazon_default_tag'),
      tagsPrefix: stringSetting(settings, 'ecom_tags_prefix'),
      ecomAccountId: stringSetting(settings, 'ecom_account_id'),
      amazonMarket: stringSetting(settings, 'ecom_amazon_market'),
    },
    settings: publicSettingsSubset(settings),
  };
}
